import React, { useCallback, useEffect, useState, useMemo, forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Form, Input, Button, message, Alert, Typography } from 'antd';
import { NumericFormat } from 'react-number-format';
import Loader from '../loader.component';
import apiCalls from 'src/api/apiCalls';
import { validateContentRules, validateTronAddressOrHash } from 'src/utils/validation';
import { useParams } from 'react-router-dom';
const { Text } = Typography
const CustomAntdInput = forwardRef((props, ref) => (
    <Input {...props} ref={ref} />
));
CustomAntdInput.displayName = 'CustomAntdInput';
const DepositForm = ({ setIsTopupGridRefresh }) => {
    const [form] = Form.useForm();
    const [creditsAmount, setCreditsAmount] = useState({});
    const [address, setAddress] = useState(null);
    const [addressLoading, setAddressLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const { tenantId } = useParams();

    useEffect(() => {
        fetchWalletAddress();
    }, []);

    const fetchWalletAddress = async () => {
        setAddressLoading(true);
        try {
            const addr = await apiCalls.getAddress(tenantId);
            if (addr.ok) {
                setAddress(addr?.data);
            } else {
                setAddress(null);
                setErrorMessage(apiCalls.isErrorDispaly(addr));
            }
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setAddressLoading(false);
        }
    };

    // Debounce function
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    };

    const debouncedFetchTRX = useMemo(
        () => debounce(async (creditValue) => {
            if (creditValue) {
                try {
                    const result = await apiCalls.getCreditsAmount(Number(creditValue));
                    setCreditsAmount(result?.data);
                } catch (error) {
                    setErrorMessage(error.message);
                }
            } else {
                setCreditsAmount({});
            }
        }, 200), // 200ms debounce delay
        [] // Empty dependency array means this debounced function is created once
    );

    const handleCreditChange = useCallback(async (e) => {
        const value = e.target.value.replace(/\D/g, ''); // Only numbers
        form.setFieldsValue({ creditNumber: value });
        setCreditsAmount({});
        debouncedFetchTRX(value);
    }, [form, debouncedFetchTRX]);

    const onFinish = useCallback(async (values) => {
        try {
            if (!address?.address) {
                setErrorMessage("There is no deposit address.")
            } else {
                setLoading(true);
                const obj = {
                    "credits": values?.creditNumber,
                    "amount": creditsAmount?.amount,
                    "currency": creditsAmount?.currency,
                    "address": address?.address,
                    "transactionhash": values?.hash,
                    "remarks": values?.remarks
                }
                const response = await apiCalls?.depositSave(obj, tenantId)
                if (response.ok) {
                    form.resetFields();
                    setCreditsAmount({});
                    fetchWalletAddress();
                    setIsTopupGridRefresh(true);
                    message.success({
                        content: "Credits deposited successfully!",
                        className: "custom-msg",
                        duration: 3,
                    });
                } else {
                    setErrorMessage(apiCalls.isErrorDispaly(response));
                }
            }
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setLoading(false);
        }
    }, [creditsAmount, form, address])
    const closeError = useCallback(() => {
        setErrorMessage(null);
    }, []);
    return (

        <>
            <div className="">
                <div className="justify-content-center energy-deposit-form">
                    <div className="">
                        <div className="summary-container p-3 text-white rounded">
                            <h2 className="mb-4 text-center main-title">Deposit TRX Into This Address</h2>

                            {addressLoading && <Loader />}

                            {!addressLoading && (
                                <>
                                    {errorMessage && (
                                        <Alert
                                            className="mb-3"
                                            type="error"
                                            description={errorMessage}
                                            onClose={closeError}
                                            showIcon
                                            closable
                                            style={{ backgroundColor: '#1f1f1f', color: '#ffffff' }}
                                        />
                                    )}

                                    <Form form={form} layout="vertical" onFinish={onFinish}>
                                        <div className='cust-input cust-textArea custom-input-field mt-22'>
                                            <Form.Item
                                                className='input-label floatinput-label mb-4 custom-margin'
                                                label="Credit number"
                                                name="creditNumber"
                                                rules={[{ required: true, message: 'Is required' }]}
                                            >
                                                <NumericFormat
                                                    customInput={CustomAntdInput}
                                                    thousandSeparator
                                                    className="form-control text-white"
                                                    placeholder="Enter credit number"
                                                    onChange={handleCreditChange}
                                                    maxLength={3}
                                                />
                                            </Form.Item>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center amount-bg text-white rounded p-3 mb-4">
                                            <span>TRX</span>
                                            <span>{creditsAmount?.amount || '0'}</span>
                                        </div>

                                        <div className="text-center my-4">
                                            <QRCodeSVG
                                                value={address?.address || '--'}
                                                size={120}
                                                height={100}
                                                width={100}
                                                className="qr-image mx-auto"
                                            />
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center amount-bg text-white rounded p-3 mb-4">
                                            <span>Deposit Address</span>
                                            <Text
                                                copyable={{ text: address?.address, tooltips: ['Copy', 'Copied'] }}
                                                className="text-white text-truncate"
                                                style={{ maxWidth: '60%' }}
                                            >
                                                {address?.address
                                                    ? `${address?.address.slice(0, 8)}.....${address?.address.slice(-8)}`
                                                    : '--'}
                                            </Text>
                                        </div>

                                        <div className='cust-input cust-textArea custom-input-field mt-22 '>
                                            <Form.Item
                                                className='input-label floatinput-label mb-4 custom-margin'
                                                label="Hash"
                                                name="hash"
                                                rules={[
                                                    { required: true, message: 'Is required' },
                                                    { validator: validateContentRules },
                                                    { validator: validateTronAddressOrHash },
                                                ]}
                                            >
                                                <Input
                                                    className="form-control bg-dark text-white"
                                                    placeholder="Enter hash"
                                                    autoComplete="off"
                                                />
                                            </Form.Item>
                                        </div>

                                        <div className='cust-input cust-textArea custom-input-field mt-22'>
                                            <Form.Item
                                                className='input-label floatinput-label mb-4 custom-margin'
                                                label="Remarks"
                                                name="remarks"
                                                rules={[{ validator: validateContentRules }]}
                                            >
                                                <Input
                                                    className="form-control bg-dark text-white"
                                                    placeholder="Remarks"
                                                    autoComplete="off"
                                                />
                                            </Form.Item>
                                        </div>

                                        <div>
                                            <Form.Item>
                                                <Button
                                                    type="primary"
                                                    htmlType="submit"
                                                    loading={loading}
                                                    className="w-100 primary-btn"
                                                >
                                                    Save
                                                </Button>
                                            </Form.Item>
                                        </div>
                                    </Form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
};
export default DepositForm;
